import React, { createContext, useContext, useState, useEffect } from 'react';
import { CircularProgress, Box, Typography } from '@mui/material';
import {
    loadWhitelabelConfig,
    getCachedWhitelabel,
    updateDocumentMeta,
    getDefaultWhitelabelConfig,
    isValidWhitelabelConfig
} from '../utils/whitelabelUtils';

// Create the Whitelabel Context
const WhitelabelContext = createContext({
    config: null,
    loading: true,
    error: null,
    refreshConfig: () => {}
});

/**
 * Custom hook to use Whitelabel Context
 */
export const useWhitelabel = () => {
    const context = useContext(WhitelabelContext);
    if (!context) {
        throw new Error('useWhitelabel must be used within WhitelabelProvider');
    }
    return context;
};

/**
 * Whitelabel Provider Component
 * Loads whitelabel configuration on mount and provides it to the app
 */
export const WhitelabelProvider = ({ children }) => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadConfig = async () => {
        try {
            setLoading(true);
            setError(null);

            // Get current domain
            const domain = window.location.hostname;
            console.log('Loading whitelabel config for domain:', domain);

            // Try to get cached config first
            const cachedConfig = getCachedWhitelabel();
            if (cachedConfig && isValidWhitelabelConfig(cachedConfig)) {
                console.log('Using cached whitelabel config');
                setConfig(cachedConfig);
                updateDocumentMeta(cachedConfig);
                setLoading(false);
                
                // Load fresh config in background
                loadFreshConfig(domain);
                return;
            }

            // Load fresh config
            await loadFreshConfig(domain);
        } catch (err) {
            console.error('Error in loadConfig:', err);
            handleConfigError(err);
        }
    };

    const loadFreshConfig = async (domain) => {
        try {
            const freshConfig = await loadWhitelabelConfig(domain);
            
            if (freshConfig && isValidWhitelabelConfig(freshConfig)) {
                setConfig(freshConfig);
                updateDocumentMeta(freshConfig);
                setError(null);
            } else {
                // Use default configuration if no config found
                console.log('No whitelabel config found, using defaults');
                const defaultConfig = getDefaultWhitelabelConfig();
                setConfig(defaultConfig);
                updateDocumentMeta(defaultConfig);
            }
        } catch (err) {
            console.error('Error loading fresh config:', err);
            // Don't throw error if we already have cached config
            if (!config) {
                handleConfigError(err);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConfigError = (err) => {
        console.error('Failed to load whitelabel config, using defaults');
        const defaultConfig = getDefaultWhitelabelConfig();
        setConfig(defaultConfig);
        updateDocumentMeta(defaultConfig);
        setError(err.message || 'Failed to load branding configuration');
        setLoading(false);
    };

    const refreshConfig = async () => {
        await loadConfig();
    };

    useEffect(() => {
        loadConfig();
    }, []);

    // Show loading screen while initial config is loading
    if (loading && !config) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    color: 'white'
                }}
            >
                <CircularProgress 
                    size={60} 
                    thickness={4} 
                    sx={{ color: 'white', mb: 3 }} 
                />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Loading Application...
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    Please wait while we customize your experience
                </Typography>
            </Box>
        );
    }

    const contextValue = {
        config,
        whitelabelConfig: config, // Alias for backward compatibility
        loading,
        error,
        refreshConfig
    };

    return (
        <WhitelabelContext.Provider value={contextValue}>
            {children}
        </WhitelabelContext.Provider>
    );
};

export default WhitelabelProvider;
